/**
 * ScanUploader - Upload and process warehouse floor plan scans
 */
import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Slider,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ScanResult, DetectedElement } from './types';
import { API_BASE_URL } from '../../utils/apiConfig';

interface ScanUploaderProps {
  warehouseId: number;
  onScanComplete: (result: ScanResult, acceptedElements: DetectedElement[]) => void;
  onClose: () => void;
}

const ScanUploader: React.FC<ScanUploaderProps> = ({
  warehouseId,
  onScanComplete,
  onClose,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [selectedElements, setSelectedElements] = useState<Set<string>>(new Set());
  const [showSettings, setShowSettings] = useState(false);

  // Detection parameters
  const [minArea, setMinArea] = useState(500);
  const [maxArea, setMaxArea] = useState(100000);
  const [scaleWidth, setScaleWidth] = useState(1000);
  const [excludeAisles, setExcludeAisles] = useState(true); // Default: exclude aisles

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      setError(t('warehouseMap.scan.fileTypeError'));
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError(t('warehouseMap.scan.fileSizeError'));
      return;
    }

    setFile(selectedFile);
    setError(null);
    setScanResult(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  }, [t]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const input = fileInputRef.current;
      if (input) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(droppedFile);
        input.files = dataTransfer.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, []);

  // Process scan
  const handleScan = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('min_area', String(minArea));
      formData.append('max_area', String(maxArea));
      formData.append('scale_to_width', String(scaleWidth));

      // API_BASE_URL is imported at the top

      // Use axios instead of fetch to benefit from token refresh interceptor
      const response = await axios.post(
        `${API_BASE_URL}/warehouses/${warehouseId}/scan_map/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const rawResult = response.data;

      // Map snake_case from API to camelCase for frontend
      const mappedElements = (rawResult.detected_elements || []).map((el: any) => ({
        id: el.id,
        position: el.position,
        dimensions: el.dimensions,
        rotation: el.rotation || 0,
        area: el.area,
        confidence: el.confidence,
        suggestedType: el.suggested_type || el.suggestedType,
        suggestedColor: el.suggested_color || el.suggestedColor,
        vertices: el.vertices,
      }));

      // Filter out AISLE if excludeAisles is true
      const filteredElements = excludeAisles
        ? mappedElements.filter((el: any) => el.suggestedType !== 'AISLE')
        : mappedElements;

      const result: ScanResult = {
        success: rawResult.success ?? true,
        mapDimensions: rawResult.map_dimensions || {
          width: rawResult.map_dimensions?.width || 1000,
          height: rawResult.map_dimensions?.height || 800,
          originalWidth: rawResult.map_dimensions?.original_width || 1000,
          originalHeight: rawResult.map_dimensions?.original_height || 800,
          scaleFactor: rawResult.map_dimensions?.scale_factor || 1,
        },
        detectedElements: filteredElements,
        elementsCount: filteredElements.length,
        previewImage: rawResult.preview_image || '',  // Backend already includes data:image/png;base64 prefix
        warehouse: rawResult.warehouse,
      };

      setScanResult(result);

      // Select all high-confidence elements by default
      const highConfidence = result.detectedElements
        .filter(el => el.confidence > 60)
        .map(el => el.id);
      setSelectedElements(new Set(highConfidence));

    } catch (err: any) {
      console.error('Scan error:', err);

      // Handle axios errors
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.detail ||
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          t('warehouseMap.scan.unknownError');

        // Check if it's a token expiration error (should be handled by interceptor, but just in case)
        if (err.response?.status === 401) {
          setError(t('warehouseMap.scan.sessionExpired'));
        } else if (err.response?.status) {
          setError(t('warehouseMap.scan.serverError', { status: err.response.status, message: errorMessage }));
        } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
          setError(t('warehouseMap.scan.networkError'));
        } else {
          setError(errorMessage || t('warehouseMap.scan.scanError'));
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : t('warehouseMap.scan.unknownError');
        setError(errorMessage || t('warehouseMap.scan.scanError'));
      }

      // Clear scan result on error
      setScanResult(null);
      setSelectedElements(new Set());
    } finally {
      setLoading(false);
    }
  };

  // Toggle element selection
  const toggleElement = (id: string) => {
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select/deselect all
  const selectAll = () => {
    if (scanResult) {
      setSelectedElements(new Set(scanResult.detectedElements.map(el => el.id)));
    }
  };

  const deselectAll = () => {
    setSelectedElements(new Set());
  };

  // Confirm selection
  const handleConfirm = () => {
    if (scanResult) {
      const acceptedElements = scanResult.detectedElements.filter(
        el => selectedElements.has(el.id)
      );
      onScanComplete(scanResult, acceptedElements);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          backgroundColor: '#2a2a2a',
          color: '#ffffff',
          opacity: 1,
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#ffffff',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImageIcon />
          <Typography variant="h6" sx={{ color: '#ffffff' }}>{t('warehouseMap.scan.title')}</Typography>
        </Box>
        <Tooltip title={t('warehouseMap.scan.settings')}>
          <IconButton onClick={() => setShowSettings(!showSettings)} sx={{ color: '#ffffff' }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#2a2a2a', color: '#ffffff', opacity: 1 }}>
        {/* Settings panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#2a2a2a', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#ffffff' }}>
                  {t('warehouseMap.scan.detectionParameters')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#ffffff' }}>
                      {t('warehouseMap.scan.minArea')}: {minArea}px²
                    </Typography>
                    <Slider
                      value={minArea}
                      onChange={(_, v) => setMinArea(v as number)}
                      min={100}
                      max={5000}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#ffffff' }}>
                      {t('warehouseMap.scan.maxArea')}: {maxArea}px²
                    </Typography>
                    <Slider
                      value={maxArea}
                      onChange={(_, v) => setMaxArea(v as number)}
                      min={10000}
                      max={500000}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#ffffff' }}>
                      {t('warehouseMap.scan.mapWidth')}: {scaleWidth}px
                    </Typography>
                    <Slider
                      value={scaleWidth}
                      onChange={(_, v) => setScaleWidth(v as number)}
                      min={500}
                      max={2000}
                      step={100}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={excludeAisles}
                        onChange={(e) => setExcludeAisles(e.target.checked)}
                        size="small"
                      />
                    }
                    label={t('warehouseMap.scan.excludeAisles')}
                    sx={{ color: '#ffffff' }}
                  />
                </Box>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* File upload area */}
        {!scanResult && (
          <Paper
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: file ? 'primary.main' : 'rgba(255, 255, 255, 0.3)',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: file ? 'rgba(46, 125, 50, 0.2)' : '#2a2a2a',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(46, 125, 50, 0.2)',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {preview ? (
              <Box>
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 8,
                    marginBottom: 16,
                  }}
                />
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {file?.name} ({(file?.size || 0 / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              </Box>
            ) : (
              <Box>
                <UploadIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.7)', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>
                  {t('warehouseMap.scan.dragFile')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('warehouseMap.scan.clickToSelect')}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#ffffff' }}>
              {t('warehouseMap.scan.analyzing')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('warehouseMap.scan.detecting')}
            </Typography>
          </Box>
        )}

        {/* Scan results */}
        {scanResult && !loading && (
          <Box>
            {/* Preview image with detections */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#2a2a2a', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#ffffff' }}>
                  {t('warehouseMap.scan.elementsDetected', { count: scanResult.elementsCount })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={selectAll} sx={{ color: '#ffffff' }}>
                    {t('warehouseMap.scan.selectAll')}
                  </Button>
                  <Button size="small" onClick={deselectAll} sx={{ color: '#ffffff' }}>
                    {t('warehouseMap.scan.deselectAll')}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleScan}
                    sx={{ color: '#ffffff' }}
                  >
                    {t('warehouseMap.scan.scanAgain')}
                  </Button>
                </Box>
              </Box>

              <img
                src={scanResult.previewImage}
                alt="Scan result"
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                }}
              />
            </Paper>

            {/* Detected elements list */}
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#ffffff' }}>
              {t('warehouseMap.scan.selectElements', { count: selectedElements.size })}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 1,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {scanResult.detectedElements.map((element) => (
                <Paper
                  key={element.id}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: selectedElements.has(element.id) ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                    backgroundColor: selectedElements.has(element.id) ? 'rgba(46, 125, 50, 0.3)' : '#2a2a2a',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                    },
                  }}
                  onClick={() => toggleElement(element.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Checkbox
                      checked={selectedElements.has(element.id)}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Chip
                      label={t(`warehouseMap.elementTypes.${element.suggestedType}`)}
                      size="small"
                      sx={{ backgroundColor: element.suggestedColor }}
                    />
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#ffffff' }} display="block">
                      {t('warehouseMap.scan.confidence')}: {element.confidence.toFixed(0)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={element.confidence}
                      color={element.confidence > 70 ? 'success' : 'warning'}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                    <Typography variant="caption" sx={{ color: '#ffffff', mt: 0.5 }} display="block">
                      {Math.round(element.dimensions.width)} × {Math.round(element.dimensions.height)} px
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: '#2a2a2a', borderTop: '1px solid rgba(255, 255, 255, 0.1)', opacity: 1 }}>
        <Button onClick={onClose} sx={{ color: '#ffffff' }}>
          {t('warehouseMap.scan.cancel')}
        </Button>

        {!scanResult ? (
          <Button
            variant="contained"
            onClick={handleScan}
            disabled={!file || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <ImageIcon />}
          >
            {loading ? t('warehouseMap.scan.scanning') : t('warehouseMap.scan.scan')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedElements.size === 0}
            startIcon={<CheckIcon />}
          >
            {t('warehouseMap.scan.createElements', { count: selectedElements.size })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ScanUploader;

